/**
 * Client-side request-path normalization for {@link createFetcher}.
 *
 * Two defect classes this module exists to kill (both reached production):
 *
 * 1. **Trailing-slash 307 -> CORS.** FastAPI's `redirect_slashes` answers a
 *    slash-mismatched request (`/api/x` against a route declared `/api/x/`) with a
 *    307 whose `Location` is the ABSOLUTE api host. Behind a same-origin proxy that
 *    redirect becomes cross-origin and the browser blocks it on CORS, so the SPA
 *    sees a network error with no usable message (data app `useJobs`, 2026-10-19).
 *    The client half of the fix is {@link TrailingSlashPolicy}: let the app declare
 *    the server's policy once instead of hand-typing the slash at every call site.
 *
 * 2. **`/api/api` prefix doubling.** A `baseURL` that already ends in `/api` (for
 *    example `VITE_API_URL=/api`) combined with a path that starts with `/api/`
 *    requests `/api/api/...` and 404s every call. The scaffold template shipped
 *    exactly this default. {@link findDoubledApiPrefix} detects it and
 *    {@link ApiPathError} is thrown instead of sending a request that cannot work.
 *
 * Pure string functions only: no axios, no DOM, no Node APIs. This file is bundled
 * by the Expo mobile fleet from source, so it must stay plain TypeScript (no
 * `declare` class fields, see `mobile-babel-compat.test.ts`).
 */

/**
 * How the client treats the trailing slash of the PATH part of every request URL.
 *
 * - `"preserve"` (default): send the path exactly as written. Correct for the
 *   fleet today, where each call site spells the slash its route declares and
 *   contract tests (see `path-contract.ts`) pin it. Changing nothing is the only
 *   default that cannot break a currently-working app.
 * - `"add"`: guarantee a trailing slash. Use when the server declares every route
 *   with a trailing slash (FastAPI routers mounted with `"/"` leaf routes).
 * - `"remove"`: strip trailing slashes. Use when the server declares routes
 *   without one.
 *
 * `"add"` and `"remove"` never touch the query string or fragment, never touch a
 * bare origin or a root path, and `"add"` skips file-like last segments
 * (`/files/report.pdf`) because `/report.pdf/` is a different, wrong URL.
 */
export type TrailingSlashPolicy = "preserve" | "add" | "remove";

export type ApiPathErrorCode = "API_PATH_DOUBLE_PREFIX";

/**
 * Programmer-error thrown (rejected from the request) when the client can prove a
 * request URL is malformed before it is sent. Distinct from `ApiRequestError`
 * (a server/network failure) so callers and tests can branch on it, and so it is
 * never mistaken for "Network error — check your connection".
 */
export class ApiPathError extends Error {
  readonly code: ApiPathErrorCode;
  readonly baseURL: string;
  readonly path: string;

  constructor(code: ApiPathErrorCode, baseURL: string, path: string, message: string) {
    super(message);
    Object.setPrototypeOf(this, ApiPathError.prototype);
    Object.defineProperty(this, "name", { value: "ApiPathError", writable: true, configurable: true });
    this.code = code;
    this.baseURL = baseURL;
    this.path = path;
  }
}

/** Type guard: is this value an {@link ApiPathError}? */
export function isApiPathError(err: unknown): err is ApiPathError {
  return err instanceof ApiPathError;
}

const ABSOLUTE_URL = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;
const ORIGIN_PREFIX = /^(?:[a-z][a-z\d+\-.]*:)?\/\/[^/?#]*/i;

/** True for `https://host/x`, `//host/x`. axios ignores `baseURL` for these. */
export function isAbsoluteUrl(url: string): boolean {
  return ABSOLUTE_URL.test(url);
}

interface UrlParts {
  /** `scheme://host` (or `//host`), empty for a relative URL. */
  origin: string;
  /** Path component only (no query, no fragment). May be empty. */
  path: string;
  /** `?query` and/or `#fragment`, verbatim. */
  suffix: string;
}

function splitUrl(url: string): UrlParts {
  const originMatch = ORIGIN_PREFIX.exec(url);
  const origin = originMatch ? originMatch[0] : "";
  const rest = url.slice(origin.length);
  const cut = rest.search(/[?#]/);
  if (cut === -1) return { origin, path: rest, suffix: "" };
  return { origin, path: rest.slice(0, cut), suffix: rest.slice(cut) };
}

function pathSegments(path: string): string[] {
  return path.split("/").filter((segment) => segment.length > 0);
}

/**
 * Detect a doubled `api` prefix.
 *
 * Fires when the request URL is relative, its first path segment is `api`, and the
 * `baseURL` path already contains an `api` segment anywhere (`/api`, `/api/design`,
 * `https://host/api`, `https://host/v1/api`). A host named `api.example.com` is not
 * a path segment, so `https://api.example.com` + `/api/x` is correct and passes.
 *
 * Absolute request URLs bypass `baseURL` in axios, so they are never flagged.
 * Returns `null` when the combination is fine.
 */
export function findDoubledApiPrefix(
  baseURL: string | undefined,
  url: string | undefined
): { baseURL: string; path: string } | null {
  if (!baseURL || !url) return null;
  if (isAbsoluteUrl(url)) return null;
  const requestSegments = pathSegments(splitUrl(url).path);
  if (requestSegments[0]?.toLowerCase() !== "api") return null;
  const baseSegments = pathSegments(splitUrl(baseURL).path);
  if (!baseSegments.some((segment) => segment.toLowerCase() === "api")) return null;
  return { baseURL, path: url };
}

/** Human-readable resolution of the doubled URL, for the error message. */
function describeResolved(baseURL: string, url: string): string {
  return `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
}

/**
 * Throw {@link ApiPathError} when `baseURL` + `url` would double the `/api`
 * prefix. No-op otherwise.
 */
export function assertNoDoubledApiPrefix(baseURL: string | undefined, url: string | undefined): void {
  const hit = findDoubledApiPrefix(baseURL, url);
  if (!hit) return;
  throw new ApiPathError(
    "API_PATH_DOUBLE_PREFIX",
    hit.baseURL,
    hit.path,
    `Refusing to send "${hit.path}" with baseURL "${hit.baseURL}": it resolves to ` +
      `"${describeResolved(hit.baseURL, hit.path)}" (doubled /api prefix, a guaranteed 404). ` +
      `Either leave baseURL empty and keep the "/api/..." path, or keep the baseURL and drop "/api" from the path. ` +
      `Check VITE_API_URL: it must be unset (or a bare origin), not "/api".`
  );
}

function looksLikeFile(lastSegment: string): boolean {
  return /\.[A-Za-z0-9]{1,8}$/.test(lastSegment);
}

/**
 * Apply a {@link TrailingSlashPolicy} to the path part of a request URL.
 * `"preserve"` returns the input unchanged (same string).
 */
export function applyTrailingSlashPolicy(url: string, policy: TrailingSlashPolicy): string {
  if (policy === "preserve") return url;
  const { origin, path, suffix } = splitUrl(url);
  // Nothing to normalize: empty URL, bare origin, or the root path.
  if (path === "" || path === "/") return url;
  if (policy === "remove") {
    const stripped = path.replace(/\/+$/, "");
    return `${origin}${stripped === "" ? "/" : stripped}${suffix}`;
  }
  // policy === "add"
  if (path.endsWith("/")) return url;
  const segments = pathSegments(path);
  const last = segments[segments.length - 1] ?? "";
  if (looksLikeFile(last)) return url;
  return `${origin}${path}/${suffix}`;
}
